import type { Product } from '../types';
import type { ProductFacts } from './productFacts';

/**
 * The questions a farmer, retailer or AI assistant actually asks about one product — answered from
 * the catalogue record and nothing else.
 *
 * EVERY ANSWER IS QUOTED, NOT WRITTEN. A question is only asked when the data that answers it exists:
 * no composition on file means no "what is it made of" entry, rather than a vague one. This is the
 * rule that matters most here — a plausible-sounding dose or mixing instruction that nobody verified
 * can cost a grower a season, so the code has no path that produces one.
 *
 * One builder, three consumers: the visible FAQ on the product page, that page's FAQPage JSON-LD, and
 * the llms.txt corpus + Markdown mirror. They can never drift apart because they are the same array.
 */
export interface FaqEntry {
  q: string;
  a: string;
}

const langName: Record<string, string> = {
  en: 'English',
  hi: 'Hindi',
  mr: 'Marathi',
  or: 'Odia',
  bn: 'Bengali',
  gu: 'Gujarati',
};

/** Lines from the sheet that speak about mixing / tank-mixing / co-application, quoted verbatim. */
function mixingLines(facts: ProductFacts): string[] {
  const re = /\b(mix|mixed|mixing|combin|co-appl|tank|alongside|together with)/i;
  return [...facts.usage, ...facts.precautions].filter((l) => re.test(l));
}

export function productFaq(product: Product, facts: ProductFacts): FaqEntry[] {
  const out: FaqEntry[] = [];
  const name = product.name;
  const add = (q: string, a?: string | null) => {
    const text = (a ?? '').replace(/\s+/g, ' ').trim();
    if (text) out.push({ q, a: text });
  };

  add(
    `What is ${name}?`,
    [product.tagline, product.shortDescription || product.purpose].filter(Boolean).join('. ').replace(/\.\./g, '.'),
  );

  const composition = [
    facts.compositionSummary,
    facts.compositionRows.map((r) => `${r.parameter}: ${r.value}`).join('; '),
  ]
    .filter(Boolean)
    .join(' ');
  add(`What is ${name} made of?`, composition);

  add(
    `Which crops is ${name} documented for?`,
    (product.recommendedCrops ?? []).length
      ? `${(product.recommendedCrops ?? []).join(', ')}. Per-crop timings and doses are listed in the dosage table on this page.`
      : '',
  );

  add(
    `How is ${name} applied?`,
    facts.applicationMethods.length ? `${facts.applicationMethods.join(', ')}.` : '',
  );

  add(
    `What is the dose of ${name}?`,
    facts.dosageSummary
      ? `${facts.dosageSummary}${facts.dosageTable?.rows.length ? ' Crop-by-crop doses and timings are in the dosage table on this page — follow the rate printed on your pack label.' : ''}`
      : facts.dosageNotes.map((r) => `${r.parameter}: ${r.value}`).join(' ') || '',
  );

  const mixing = mixingLines(facts);
  add(`Can ${name} be mixed with other inputs?`, mixing.join(' '));

  add(
    `How should ${name} be stored, and how long does it keep?`,
    [facts.packagingInfo, facts.shelfLife ? `Shelf life: ${facts.shelfLife}.` : ''].filter(Boolean).join(' '),
  );

  add(`What approval or licence does ${name} hold?`, facts.licence);

  const docs = product.resources ?? [];
  add(
    `Where can I download the ${name} technical data sheet?`,
    docs.length
      ? `The ${name} data sheet can be downloaded from the Technical documents panel on this page${
          docs.some((d) => d.lang)
            ? ` (${[...new Set(docs.map((d) => (d.lang ? langName[d.lang] ?? d.lang : 'English')))].join(' and ')})`
            : ''
        }.`
      : '',
  );

  add(
    `What pack sizes does ${name} come in?`,
    product.variants.length ? product.variants.map((v) => v.packSize).join(', ') + '.' : '',
  );

  return out;
}
